import * as React from 'react';
import { Form, Modal, ModalBody, ModalFooter, CustomDatePicker, NumberInput, CustomButton, ButtonType, CustomSubmitButton, ModalSize, Select, AlertMessage, AlertType } from "soprasteria-ui-library";
import { ITreatmentReportTreatment } from '../../../models/ITreatmentReport';
import { TreatmentsDropdown } from './TreatmentsDropdown';
import { useAuth } from '../../../App';
import { CarePerformerApi } from '../../../api/CarePerformerApi';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { addToMonth, formatDate } from '../../../helpers/DateHelper';
import { setMessage } from '../../../helpers/MessageHelper';
import { NoCarePerformersFoundMessage } from '../../../utils/CarePerformerConstants';
import { TreatmentReportApi } from '../../../api/TreatmentReportApi';

interface ITreatmentsModal {
    patientId: number;
    onSubmit: (treatment: ITreatmentReportTreatment, onCallEnd?: () => void) => void;
    closeModal: () => any;
}

export const TreatmentsModal: React.FunctionComponent<ITreatmentsModal> = ({ patientId, onSubmit, closeModal }) => {
    const auth = useAuth();
    const api = new TreatmentReportApi(auth);
    const carePerformerApi = new CarePerformerApi(auth);
    const [isFormPending, setIsFormPending] = React.useState<boolean>(false);
    const [assessmentDate, setAssessmentDate] = React.useState<Date>(new Date());
    const [carePerformers, setCarePerformers] = React.useState<IListItem[]>([]);
    const [selectedCarePerformerId, setSelectedCarePerformerId] = React.useState<number>();
    const [noCarePerformersMessage, setNoCarePerformersMessage] = React.useState<string>();
    const [isLoadingCarePerformers, setIsLoadingCarePerformers] = React.useState<boolean>(false);
    const [treatmentCodes, setTreatmentCodes] = React.useState<ITreatmentReportTreatment[]>([]);
    const dateToday = new Date();

    React.useEffect(() => {
        document.getElementById("open-treatment-modal")?.click();
    }, [patientId]);

    React.useEffect(() => {
        if (assessmentDate) {
            loadCarePerformers();
        }
    }, [assessmentDate])

    React.useEffect(() => {
        if (selectedCarePerformerId) {
            loadTreatmentCodes();
        }
    }, [selectedCarePerformerId])

    const loadCarePerformers = () => {
        setIsLoadingCarePerformers(true);
        carePerformerApi.getCarePerformersWithDate(formatDate(assessmentDate), (carePerformersData) => {
            let mappedCarePerformers = carePerformersData.map<IListItem>((carePerformer) => { return { text: carePerformer.carePerformerName, value: carePerformer.carePerformerId.toString() } });
            setCarePerformers(mappedCarePerformers);
            if (mappedCarePerformers.length === 0) {
                const newMessage = setMessage(NoCarePerformersFoundMessage, noCarePerformersMessage ?? "");
                setNoCarePerformersMessage(newMessage);
            } else {
                setNoCarePerformersMessage("");
            }
        }, undefined, () => setIsLoadingCarePerformers(false));
    }

    const onFormSubmit = (data: any) => {
        setIsFormPending(true);
        onSubmit(data, () => {
            setIsFormPending(false);
        });
    }

    const onAssessmentDateChange = (newAssessmentDate: Date | null) => {
        if (newAssessmentDate === null) {
            setAssessmentDate(new Date());
        } else {
            setAssessmentDate(newAssessmentDate);
        }
    }

    const loadTreatmentCodes = () => {
        if (assessmentDate && selectedCarePerformerId) {
            api.getTreatmentCodes(patientId, selectedCarePerformerId, formatDate(assessmentDate), (codes: ITreatmentReportTreatment[]) => {
                setTreatmentCodes(codes);
            });
        }
    }

    return (
        <Modal id="treatment-modal" modalSize={ModalSize.large} header="Lägg till åtgärdskod">
            <Form onSubmit={onFormSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={10000}>{noCarePerformersMessage}</AlertMessage>
                    <div className="row">
                        <div className="col-12">
                            <CustomDatePicker
                                label="Behandlingsdatum"
                                name="date"
                                value={assessmentDate}
                                min={addToMonth(assessmentDate, -3)}
                                max={dateToday}
                                onChange={onAssessmentDateChange}
                                tooltipDescription="Avser datumet när behandlingsåtgärden utfördes på patienten, ska överensstämma med journalen"
                                required
                                inlineLabel />
                            <Select name="carePerformer.carePerformerId"
                                label="Behandlare"
                                placeholder="Välj en behandlare"
                                options={carePerformers}
                                isLoading={isLoadingCarePerformers}
                                onChange={(val) => setSelectedCarePerformerId(val ? parseInt(val) : undefined)}
                                required
                                inlineLabel />
                            <TreatmentsDropdown label="Åtgärdskod" treatments={treatmentCodes} disabled={selectedCarePerformerId === undefined} name="treatmentCodeId" />
                            <NumberInput minValue={1} defaultValue="1" label="Antal" name="quantity" required inlineLabel />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} id="close-treatment-modal" data-dismiss="modal" onClick={() => closeModal()} buttonText="Avbryt" />
                        <CustomSubmitButton isLoading={isFormPending}>Spara</CustomSubmitButton>
                        <input type="hidden" data-toggle="modal" id="open-treatment-modal" data-target="#treatment-modal" />
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    )
}